import type * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-transparent-black-30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends React.ComponentProps<
  typeof DialogPrimitive.Content
> {
  closeLabel: string;
  showCloseButton?: boolean;
}

export function DialogContent({
  className,
  children,
  closeLabel,
  showCloseButton = true,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          // no max-h/overflow here: MobileNav repurposes this as a full-height
          // h-dvh off-canvas panel, and max-height would fight that. Callers
          // needing inner scroll on a tall centered dialog opt in via className.
          // Every popup is at least 500px wide from md and 326px tall on
          // desktop; content-between spends the extra height between the
          // rows (header top, actions bottom) instead of stretching them.
          "fixed start-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-2rem)] -translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 content-between gap-4 rounded-md border border-container-default-border bg-container-default-background p-6 text-text shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 md:min-w-[500px] md:max-w-lg lg:min-h-[326px]",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="absolute end-4 top-4 flex size-8 items-center justify-center rounded-full bg-container-hover text-text transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--focus-ring,var(--color-primary))] disabled:pointer-events-none disabled:opacity-40">
            <X className="size-4" strokeWidth={3} aria-hidden="true" />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export interface DialogHeaderCloseProps extends Omit<
  React.ComponentProps<typeof DialogPrimitive.Close>,
  "children"
> {
  label: string;
}

/**
 * The close control for a popup's coloured header bar: a white circle with a
 * bold X in the header's colour. Popups tinted red or orange set
 * `--focus-ring` to that colour on their content; the rest are brand purple.
 */
export function DialogHeaderClose({
  label,
  className,
  ...props
}: DialogHeaderCloseProps) {
  return (
    <DialogPrimitive.Close
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-[color:var(--focus-ring,var(--color-primary-button-background))] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
      {...props}
    >
      <X className="size-4" strokeWidth={3} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </DialogPrimitive.Close>
  );
}

export function DialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-start",
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        // Cancel comes first and stays on the left in Arabic too: RTL reverses
        // the row, and justify-start then keeps the group on the left.
        "flex flex-col-reverse gap-2 [&>*]:w-full sm:flex-row sm:justify-end sm:gap-2 sm:[&>*]:w-auto sm:rtl:flex-row-reverse sm:rtl:justify-start",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn(
        "text-heading-h6-semibold leading-none tracking-tight text-text",
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-body-s-r text-text-placeholder", className)}
      {...props}
    />
  );
}
